/*
 * File: NextLastAppointments.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Shows and styles the next and last appointment list for the currently selected appointment
 */
import React from "react";
import ApptStore from "services/appointments/store/DataStore";
import { Toolbar, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import PatientChart from "services/patients/topofthechart/PatientChart";
import PropTypes from "prop-types";
import MainHeader from "components/MainHeader";
import UILinkList from "components/ui/UILinkList";
import DateFormatter from "utils/DateFormatter";
import { format, parseISO } from "date-fns";
import { useStore } from "utils/useStore";

const styles = theme => ({
  toolbar: {
    padding: theme.spacing(2.5),
    display: "block",
    marginBottom: theme.spacing(-1),
    marginTop: theme.spacing(2.5)
  },
  divider: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2)
  },
  noData: {
    color: theme.palette.common.subtitle,
    fontStyle: "italic"
  }
});

const NextLastAppointments = ({ classes }) => {
  const selectedAppt = useStore(ApptStore).selectedAppt;

  const formatNoData = name => {
    return (
      <Typography className={classes.noData}>No {name} Appointment</Typography>
    );
  };

  const formatLastAppt = () => {
    if (selectedAppt.lastAppointment) {
      const date = DateFormatter.mdy(selectedAppt.lastAppointment);
      const time = format(parseISO(selectedAppt.lastAppointment), "h:mm a");
      return {
        primaryString: "Last Appointment",
        secondaryString: date + " " + time,
        url: "/appointments/" + selectedAppt.lastAppointmentId
      };
    } else {
      return {
        primaryString: formatNoData("Last")
      };
    }
  };

  const formatNextAppt = () => {
    if (selectedAppt.nextAppointment) {
      const date = DateFormatter.mdy(selectedAppt.nextAppointment);
      const time = format(parseISO(selectedAppt.nextAppointment), "h:mm a");
      return {
        primaryString: "Next Appointment",
        secondaryString: date + " " + time,
        url: "/appointments/" + selectedAppt.nextAppointmentId
      };
    } else {
      return {
        primaryString: formatNoData("Next")
      };
    }
  };

  const links = [formatLastAppt(), formatNextAppt()];
  return (
    <React.Fragment>
      <PatientChart />
      <Toolbar className={classes.toolbar}>
        <MainHeader
          pageTitle="Last & Next Appointments"
          pageSubtitle="Appointment Details"
        />
      </Toolbar>

      <UILinkList links={links} includeFabPadding />
    </React.Fragment>
  );
};

NextLastAppointments.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(NextLastAppointments);
